import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X } from "lucide-react";
import { driveThumb, drivePreview } from "@/lib/lumiqoData";

/**
 * Universal lightbox: direct MP4 videos (`src`), Google Drive videos (`driveId`)
 * and Drive images.
 * Props:
 *   open, onOpenChange  — controlled dialog
 *   media               — { kind: "video" | "image", src?, driveId?, title, aspect? }
 *     aspect: "16/9" (default for image), "9/16" (vertical), "auto"
 */
const MediaLightbox = ({ open, onOpenChange, media }) => {
  if (!media) return null;
  const { kind, src, driveId, title, aspect = "16/9" } = media;
  const aspectClass =
    aspect === "9/16"
      ? "aspect-[9/16] max-h-[85vh]"
      : aspect === "16/9"
        ? "aspect-video"
        : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] p-0 bg-[#0a1d4d] border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
        data-testid="media-lightbox"
      >
        <button
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition-colors"
          aria-label="Close"
          data-testid="media-lightbox-close"
        >
          <X className="w-4 h-4" />
        </button>

        {kind === "video" ? (
          <div className={`relative w-full ${aspectClass} bg-black`}>
            {open &&
              (src ? (
                <video
                  key={src}
                  src={src}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-contain bg-black"
                  data-testid="media-lightbox-video"
                />
              ) : (
                <iframe
                  key={driveId}
                  src={drivePreview(driveId)}
                  title={title}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                  data-testid="media-lightbox-iframe"
                />
              ))}
          </div>
        ) : (
          <div className="relative w-full bg-black flex items-center justify-center">
            <img
              src={driveThumb(driveId, 1600)}
              alt={title}
              className="w-full h-auto max-h-[85vh] object-contain"
              data-testid="media-lightbox-image"
            />
          </div>
        )}

        {title && (
          <div className="px-5 py-3 bg-[#0a1d4d] text-white/85 text-sm font-medium font-display tracking-tight border-t border-white/10">
            {title}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MediaLightbox;
